import * as React from "react";

export const useMultipleSelection = <T = string | number>(defaultSelected?: T[], options?: T[]) => {
  const [selected, setSelected] = React.useState(defaultSelected || []);

  const _unselect = React.useCallback((state: T[], key: T) => {
    return [...state].filter((_key) => _key !== key);
  }, []);

  const select = React.useCallback(
    (key: T) => {
      setSelected((prevState) => {
        const newState = [...prevState];
        if (prevState.indexOf(key) >= 0) {
          return _unselect(newState, key);
        } else {
          newState.push(key);
          return newState;
        }
      });
    },
    [_unselect]
  );

  const selectList = React.useCallback(
    (keys: T[], withToggle?: boolean) => {
      const _withToggle = withToggle === undefined ? true : withToggle;

      setSelected((prevState) => {
        let newState = [...prevState];
        keys.forEach((key) => {
          if (_withToggle && newState.indexOf(key) >= 0) {
            newState = _unselect(newState, key);
          } else {
            newState.push(key);
          }
        });
        return newState;
      });
    },
    [_unselect]
  );

  const unselectList = React.useCallback(
    (keys: T[]) => {
      setSelected((prevState) => {
        let newState = [...prevState];
        keys.forEach((key) => {
          if (newState.indexOf(key) >= 0) {
            newState = _unselect(newState, key);
          }
        });
        return newState;
      });
    },
    [_unselect]
  );

  const setSelectedList = React.useCallback((selected: T[]) => {
    setSelected(selected);
  }, []);

  const unselectAll = React.useCallback(() => {
    setSelected([]);
  }, []);

  const selectAll = React.useCallback(() => {
    if (!options) return;
    setSelected(options);
  }, [options]);

  const isSelected = React.useCallback((key: T) => selected.indexOf(key) >= 0, [selected]);

  return { isSelected, select, setSelectedList, unselectList, selectList, unselectAll, selectAll, selected };
};
